import { useEffect, useMemo, useState } from 'react'

import { format, parseISO } from 'date-fns'

import Search from '../../../../components/ui/search'
import DatePicker from '../../../../components/ui/transaction-history/components/date-picker'

import { CopyButtonWithSuccess } from '../../../../components/ui/copy-button-with-success'

import BorderedButton from '../../../../components/global/bordered-button'

import { EReferralRewardStatusLoan, IUserReferralsLoan } from '../../../../store/fetchers/users/types'

import { formatNumber } from '../../../../utils/formatters'

import { floorNumber } from '../../../../utils/floor-number'

import DepositCard from '../../components/deposit-card'

import { FilterList } from '../../../../components/ui/filter-modal-list'

import { AddFilterPopup } from '../../components/add-filter-popup'

import styles from './styles.module.scss'
import ReferralIcon from './icons/referral-icon.svg'
import FilterBlueIcon from './icons/filter-blue-icon.svg'
import DownloadIcon from './icons/download-icon.svg'
import { ReferralRewardsProps } from './referral-rewards.props'
import EmptyIcon from './icons/no-rewards.svg'
import { useTranslation } from 'utils/use-translation'

export enum ERewardStatusView {
  PENDING = 'Pending',
  AVAILABLE = 'Available',
  WITHDRAWN = 'Withdrawn',
}

interface GroupedLoans {
  date: string
  data: IUserReferralsLoan[]
}

function groupLoansByDate(rewards: IUserReferralsLoan[]): GroupedLoans[] {
  const grouped = rewards.reduce<Record<string, IUserReferralsLoan[]>>((acc, reward) => {
    const date = format(parseISO(reward.created_at), 'd MMM, yyyy')
    if (!acc[date]) {
      acc[date] = []
    }
    acc[date].push(reward)
    return acc
  }, {})

  return Object.entries(grouped).map(([date, data]) => ({ date, data }))
}

const matchStatus = (status: EReferralRewardStatusLoan): ERewardStatusView => {
  switch (status) {
    case EReferralRewardStatusLoan.NOT_READY:
      return ERewardStatusView.PENDING
    case EReferralRewardStatusLoan.READY:
      return ERewardStatusView.AVAILABLE
    case EReferralRewardStatusLoan.REQUESTED:
      return ERewardStatusView.AVAILABLE
    case EReferralRewardStatusLoan.COLLECTED:
      return ERewardStatusView.WITHDRAWN
  }
}

export default function ReferralRewards({ saveCSV, list }: ReferralRewardsProps) {
  const { t } = useTranslation()
  
  const [search, setSearch] = useState<string>(null)
  const [startDate, setStartDate] = useState<string>(null)
  const [endDate, setEndDate] = useState<string>(null)
  const [showCalendar, setShowCalendar] = useState<boolean>(false)
  const [groupedRewards, setGroupedRewards] = useState<GroupedLoans[]>([])
  const [filterPopupIsVisible, setFilterPopupIsVisible] = useState<boolean>(false)

  const [createdAtSort, setCreatedAtSort] = useState<0 | 1 | 2>(1)
  const [profitSort, setProfitSort] = useState<0 | 1 | 2>(0)
  const [statusFilter, setStatusFilter] = useState<ERewardStatusView[]>([])

  const [statusModalOpened, setStatusModalOpened] = useState<boolean>(false)

  const handleChangeCalendar = (val) => {
    setStartDate(val[0])
    setEndDate(val[1])
    setShowCalendar(false)
  }

  const filteredList = useMemo(() => {
    if (!list) {
      return []
    }
    let result = [...list]

    if (search) {
      result = result.filter(
        (el) =>
          el.code?.toLowerCase()?.startsWith(search?.toLowerCase()) ||
          el.network?.toLowerCase()?.startsWith(search?.toLowerCase()) ||
          el.id?.toLowerCase()?.startsWith(search?.toLowerCase()),
      )
    }

    if (startDate && endDate) {
      const start = new Date(startDate).getTime()
      const end = new Date(endDate).getTime()
      result = result.filter((item) => {
        const itemDate = new Date(item.created_at).getTime()
        return itemDate > start && itemDate < end
      })
    }

    if (statusFilter.length) {
      result = result.filter((el) => statusFilter.includes(matchStatus(el.referral_reward_status)))
    }

    if (createdAtSort === 1) {
      result = result.sort((a, b) => new Date(a.created_at).getTime() - new Date(b.created_at).getTime())
    }

    if (createdAtSort === 2) {
      result = result.sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime())
    }

    if (profitSort === 1) {
      result = result.sort((a, b) => Number(a.profit) - Number(b.profit))
    }

    if (profitSort === 2) {
      result = result.sort((a, b) => Number(b.profit) - Number(a.profit))
    }
    return result
  }, [list, createdAtSort, profitSort, statusFilter, search, startDate, endDate])

  useEffect(() => {
    setGroupedRewards(groupLoansByDate(filteredList))
  }, [filteredList])

  const handleStatusSelect = (status: string) => {
    const enumStatus = status as ERewardStatusView
    if (statusFilter.includes(enumStatus)) {
      setStatusFilter((prev) => prev.filter((el) => el !== enumStatus))
    } else {
      const statuses = [...statusFilter]
      statuses.push(enumStatus)
      setStatusFilter(statuses)
    }
  }

  const handleClearCalendar = () => {
    setEndDate(null)
    setStartDate(null)
  }

  const handleSearchChange = (val: string) => {
    setSearch(val)
  }

  return (
    <div className={styles.rewards}>
      <div className={styles.actions}>
        <Search
          placeholder={t('search_by_type')}
          isWithButton
          className={styles.search}
          value={search}
          onChange={handleSearchChange}
        />
        <DatePicker
          className={styles.date}
          startDate={startDate}
          endDate={endDate}
          handleChangeCalendar={handleChangeCalendar}
          handleClearCalendar={handleClearCalendar}
          showCalendar={showCalendar}
          setShowCalendar={setShowCalendar}
        />
        <BorderedButton className={styles.downloadBtn} onClick={saveCSV}>
          <img src={DownloadIcon} alt="download icon" />
          <p>{t('download_csv')}</p>
        </BorderedButton>
        <button onClick={() => setFilterPopupIsVisible(true)} className={styles.filterMobile}>
          <img src={FilterBlueIcon} alt="filter blue icon" />
        </button>
      </div>

      {filterPopupIsVisible && (
        <AddFilterPopup
          onClose={() => setFilterPopupIsVisible(false)}
          statusFilter={{
            statuses: [ERewardStatusView.AVAILABLE, ERewardStatusView.PENDING, ERewardStatusView.WITHDRAWN],
            selectedStatuses: statusFilter,
            onSelect: handleStatusSelect,
          }}
          sortings={[
            { label: 'Created at', state: createdAtSort, setState: setCreatedAtSort },
            { label: 'Profit', state: profitSort, setState: setProfitSort },
          ]}
        />
      )}

      <div className={styles.table}>
        <div className={styles.table__header}>
          <DepositCard.ColumnHeaderCell
            title={t('created_at')}
            onClick={() => {
              setProfitSort(0)
              setCreatedAtSort((prev) => (prev === 1 ? 2 : 1))
            }}
            className={styles.item}
            sortingState={createdAtSort}
          />

          <DepositCard.ColumnHeaderCell title={t('type')} className={styles.item} />

          <DepositCard.ColumnHeaderCell
            title={t('profit')}
            onClick={() => {
              setCreatedAtSort(0)
              setProfitSort((prev) => (prev === 1 ? 2 : 1))
            }}
            className={styles.item}
            sortingState={profitSort}
          />

          <DepositCard.ColumnHeaderCell title="ID" className={styles.item} />

          <div className={styles.wrapper}>
            <DepositCard.ColumnHeaderCell
              title={t('status')}
              onClick={() => setStatusModalOpened((prev) => !prev)}
              isActive={statusFilter.length > 0 || !!search}
              className={styles.item}
              onClearSort={() => {
                setStatusFilter([])
                setSearch('')
              }}
            />
            {statusModalOpened && (
              <FilterList
                filters={[t(ERewardStatusView.AVAILABLE.toLowerCase()), 
                  t(ERewardStatusView.PENDING.toLowerCase()), 
                  t(ERewardStatusView.WITHDRAWN.toLowerCase())]}
                selectedFilters={statusFilter}
                onSelect={handleStatusSelect}
              />
            )}
          </div>
        </div>

        {filteredList && filteredList.length !== 0 && (
          <div className={styles.table__data}>
            {filteredList.map((el) => (
              <div key={el.id} className={styles.data}>
                <span>
                  {new Date(el.created_at).toLocaleString('en-US', {
                    month: 'short', // Сокращенное название месяца (Jun)
                    day: 'numeric', // Число (2)
                    year: 'numeric', // Год (2024)
                    hour: 'numeric', // Часы (12)
                    minute: '2-digit', // Минуты (00)
                    hour12: true, // 12-часовой формат (AM/PM)
                  })}
                </span>
                <div className={styles.type}>
                  <img src={ReferralIcon} alt="referral icon" />
                  <p>{t('referral')}</p>
                </div>
                <p>
                  {formatNumber(floorNumber(Number(el.profit), 6))} {el.code?.toUpperCase()}
                </p>
                <div className={styles.id}>
                  <p>{el.id}</p>
                  <CopyButtonWithSuccess value={el.id} />
                </div>
                <span className={styles[matchStatus(el.referral_reward_status)?.toLowerCase()]}>
                  {matchStatus(el.referral_reward_status)}
                </span>
              </div>
            ))}
          </div>
        )}

        {filteredList?.length === 0 && (
          <div className={styles.empty}>
            <img src={EmptyIcon} alt="no rewards icon" />
            <p>{t('no_referrals')}</p>
          </div>
        )}
      </div>

      {filteredList && filteredList.length !== 0 && (
        <div className={styles.table_mobile}>
          {groupedRewards.map((reward) => (
            <>
              <h5>{reward.date}</h5>
              <div className={styles.items}>
                {reward.data.map((el) => (
                  <div key={el.id} className={styles.item}>
                    <div className={styles.referral}>
                      <img src={ReferralIcon} alt="referral icon" />
                      <p>{t('referral')}</p>
                    </div>
                    <p className={styles.amount}>
                      {formatNumber(floorNumber(Number(el.profit), 6))} {el?.code?.toUpperCase()}
                    </p>
                    <div className={styles.id}>
                      <p>{el.id}</p>
                      <CopyButtonWithSuccess value={el.id} />
                    </div>
                    <div className={styles.status}>
                      <p>
                        {new Date(el.created_at).toLocaleString('en-US', {
                          hour: 'numeric', // Часы
                          minute: '2-digit', // Минуты с ведущим нулём
                          hour12: true, // 12-часовой формат
                        })}
                      </p>
                      <div className={styles.divider}></div>
                      <span className={styles[matchStatus(el.referral_reward_status)?.toLowerCase()]}>
                        {matchStatus(el.referral_reward_status)}
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            </>
          ))}
        </div>
      )}
      {filteredList?.length === 0 && (
        <div className={styles.empty_mobile}>
          <img src={EmptyIcon} alt="no rewards icon" />
          <p>{t('no_referrals')}</p>
        </div>
      )}
    </div>
  )
}
